import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { Suspense, lazy, useEffect, useRef } from "react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { PageSkeleton } from "@/components/Skeleton";
import Seo from "@/components/Seo";
import PromoModal from "@/components/PromoModal";
import PromoModal2 from "@/components/PromoModal2";
import { LanguageProvider, useLanguage } from "@/lib/i18n";
import { Mail, MessageCircle, Phone, X } from "lucide-react";
import {
  getWhatsAppLink,
  NAVICARGO_EMAIL,
  NAVICARGO_EMAIL_LINK,
  NAVICARGO_PHONE,
  NAVICARGO_PHONE_LINK,
} from "@/lib/contact";
import { useState } from "react";

const Home = lazy(() => import("@/pages/Home"));
const About = lazy(() => import("@/pages/About"));
const Services = lazy(() => import("@/pages/Services"));
const ServiceDetail = lazy(() => import("@/pages/ServiceDetail"));
const Contact = lazy(() => import("@/pages/Contact"));

function AppContent() {
  const [contactOpen, setContactOpen] = useState(false);
  const location = useLocation();
  const { language } = useLanguage();
  const previousPathRef = useRef(location.pathname);
  const [promoVariant, setPromoVariant] = useState(() => (Math.random() < 0.5 ? 1 : 2));

  useEffect(() => {
    const enteredHome = location.pathname === "/" && previousPathRef.current !== "/";
    if (enteredHome) {
      setPromoVariant((current) => {
        const next = Math.random() < 0.5 ? 1 : 2;
        return next === current ? (current === 1 ? 2 : 1) : next;
      });
    }
    previousPathRef.current = location.pathname;
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-[#09090B]">
        <Seo />
        {promoVariant === 2 ? <PromoModal2 key={`promo-${language}`} /> : <PromoModal key={`promo-${language}`} />}
        <Navigation />
        <main>
          <Suspense fallback={<PageSkeleton />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/services" element={<Services />} />
              <Route path="/services/:slug" element={<ServiceDetail />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </main>
        <Footer />
        <div className="fixed bottom-4 right-4 md:bottom-5 md:right-5 z-40 flex flex-col items-end gap-3">
          {contactOpen && (
            <div
              role="dialog"
              aria-label="Opciones de contacto"
              className="contact-fab-menu w-[min(290px,calc(100vw-2rem))] rounded-2xl border border-zinc-800 bg-zinc-950/95 p-3 shadow-2xl shadow-black/40 backdrop-blur-xl"
            >
              <div className="px-3 pb-3 pt-2">
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#22C55E]">Contacto directo</p>
                <p className="mt-1 text-sm text-zinc-400">Elige cómo quieres hablar con Navicargo.</p>
              </div>
              <div className="space-y-1">
                <a
                  href={getWhatsAppLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contact-fab-action group"
                >
                  <span className="contact-fab-icon bg-[#22C55E]/10 text-[#22C55E]"><MessageCircle className="h-4 w-4" /></span>
                  <span><strong>WhatsApp</strong><small>Cotiza tu operación</small></span>
                </a>
                <a href={NAVICARGO_PHONE_LINK} className="contact-fab-action group">
                  <span className="contact-fab-icon bg-sky-400/10 text-sky-300"><Phone className="h-4 w-4" /></span>
                  <span><strong>Llamar ahora</strong><small>{NAVICARGO_PHONE}</small></span>
                </a>
                <a href={NAVICARGO_EMAIL_LINK} className="contact-fab-action group">
                  <span className="contact-fab-icon bg-violet-400/10 text-violet-300"><Mail className="h-4 w-4" /></span>
                  <span><strong>Enviar correo</strong><small>{NAVICARGO_EMAIL}</small></span>
                </a>
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={() => setContactOpen((open) => !open)}
            aria-label={contactOpen ? "Cerrar opciones de contacto" : "Abrir opciones de contacto"}
            aria-expanded={contactOpen}
            className="contact-fab flex h-12 w-12 items-center justify-center rounded-full bg-[#22C55E] text-black shadow-lg shadow-[#22C55E]/20"
          >
            {contactOpen ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
          </button>
        </div>
        <Toaster position="bottom-right" theme="dark" />
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;